// FAQ route (public storefront). EN = natural product English. Keys mirror pl/faq.ts.
// Rich-answer fragments use {b0}/{b1}… placeholders for <b> emphasis spans and {link}
// for inline links; the component supplies the JSX. Plain answers are single strings.
export const faq = {
  hero: {
    title: 'Frequently asked questions',
    // The page header carries the title and ONE way to the inbox (artboard 02).
    leadLink: 'Write to us',
  },
  cat: {
    general: 'General',
    // Rule 34: the product's currency word is the token (MAPA 37-R3).
    tokens: 'Tokens and payments',
    accounts: 'Accounts & publishing',
    plans: 'Plans',
  },
  items: {
    whatIsEdimi: {
      q: 'What exactly is EDIMI?',
      a: 'EDIMI is a {b0}fan edit generator{b1} for music artists and labels. You upload a track and pick a fragment of up to 30 seconds, and the Generator turns it into {b2}loads of unique edits{b3} - vertical, cut to the beat, with no editor and no timeline. Then EDIMI publishes them to your own account network on TikTok, Instagram and YouTube through the official APIs, on the days and at the hours you approve.',
    },
    // Video on four mechanisms (27.09), M2+M3: the question the hero line raises. No number (invariant 2).
    noMiracle: {
      q: 'Will EDIMI make me a viral hit?',
      a: 'No - and nobody honest will promise you one. There is no single best edit; what counts is the {b0}series{b1}: loads of unique edits from one track, going out regularly on your accounts on the days and at the hours you approve. You do not wait for a miracle: you build your fanbase yourself.',
    },
    // The plan merges two questions into one: both answers said the same thing —
    // where the footage comes from and that there is no editing (MAPA 37).
    whereShots: {
      q: 'Where do the clips come from, and do I need to know how to edit?',
      a: 'Two ways: you use {b0}ready-made clip packs{b1} or upload {b2}your own footage{b3} - a music video, for instance. You need no editing skills: EDIMI cuts and joins it to the beat of your track by itself, adds {b4}synced lyrics{b5} whose look you style to your brand and, if you want, opens with a {b6}hook{b7} from a ready-made pack or one of your own.',
    },
    freeStart: {
      q: 'What do I get for free to start?',
      // {n} {edits} come from FREE_START_EDITS (lib/startBonus.ts), like the landing micro line.
      a: 'When you create an account you get {b0}{n} free {edits}{b1} - no card required. You see the result on your own music before you choose a plan.',
    },
    whatIsToken: {
      q: 'What is a token?',
      // THE RATES ARE COMPOSED FROM THE API (`gem_costs`), never from this file.
      // Typed in here they promised a buyer something the pricing page and the
      // server (3 / 2 / 5) both disagreed with.
      a: 'A token is the unit you pay the app in. {b0}Generating an edit costs {render}, publishing a post {publish}, and cutting your own file into clips {cut}.{b1} A failed operation costs nothing - the tokens come back automatically.',
      // With no answer from the API the sentence stands WITHOUT numbers and points
      // where the rates are always current.
      aNoPrices: 'A token is the unit you pay the app in. The current rates for an edit, a post and cutting a file are on the {link}. A failed operation costs nothing - the tokens come back automatically.',
      link: 'pricing page',
    },
    runOut: {
      q: 'What if I run out of tokens?',
      a: 'You can {b0}buy a one-off bundle{b1} of tokens - they are valid for 24 months from purchase and stay on your account after the plan renews. The smallest bundle is available on every plan, the largest ones on Max.',
    },
    rollover: {
      q: 'Do unused tokens roll over to the next month?',
      a: 'Tokens from your plan’s monthly pool reset each billing period. Tokens from {b0}top-ups{b1} are valid for 24 months from purchase and are only used once the monthly pool is exhausted.',
    },
    payMethods: {
      q: 'Which payment methods do you support?',
      a: 'We handle payments securely through {b0}Stripe{b1}, which accepts {b2}international payment cards{b3}. VAT invoices are generated automatically.',
    },
    howMany: {
      q: 'How many edits can I generate at once?',
      a: 'Up to {b0}100 in one click{b1}, within your token pool - each with a different clip arrangement. In the Planner you pick the accounts and the times, and the “Schedule” button approves the whole plan.',
    },
    // The hero's 'account network' gets its product meaning: own accounts, official login, approval (invariant 1).
    network: {
      q: 'What is my account network?',
      a: 'Your own TikTok, Instagram and YouTube accounts - as many as you have slots for. You connect each one with {b0}the platform’s official login{b1}, and each one stays yours. Each gets its own edits: the same edit never goes out on two accounts, and posts go out within a daily limit per account. Nothing goes out without your approval, and each platform’s rules bind you just as they would without EDIMI. EDIMI does not create, sell or rent out accounts.',
    },
    howPublish: {
      q: 'How does EDIMI publish to my accounts?',
      a: 'You sign in on the platform - TikTok, Instagram or Google for YouTube - and authorize the account (OAuth), and EDIMI publishes {b0}only through the official APIs of those platforms{b1}. We never see your password. Nothing goes out without your approval: you approve a single post, or a whole plan with the “Schedule” button. No phone automation.',
    },
    howManyAccounts: {
      q: 'How many accounts can I connect?',
      a: 'It depends on your plan: Starter - 1 account, Pro - 8 accounts, Max - 40 accounts. You connect {b0}your own TikTok, Instagram and YouTube accounts{b1}. Each slot stays assigned to the same account until the billing period ends, or the end of the UTC calendar month without a plan. Disconnecting does not release it early. Reconnecting the same account needs no extra slot. You can also buy extra slots.',
    },
    changePlan: {
      q: 'Can I change my plan mid-cycle?',
      a: 'Yes. You can upgrade or downgrade at any time - billing is prorated. Your new token pool renews on the next billing date, not at the moment of the change; tokens from one-off bundles stay on your account either way.',
    },
    cancel: {
      q: 'Can I cancel my subscription?',
      a: 'Yes, yourself and online: {b0}Billing → “Manage payment” → Cancel subscription{b1} in the customer portal. No need to email us. You keep access and your plan tokens until the end of the paid period, and topped-up tokens stay on your account until the end of their 24-month validity.',
    },
    withdrawal: {
      q: 'Do I get 14 days to withdraw from the contract?',
      a: 'Yes - and it is a separate right from cancelling a subscription. Within {b0}14 days of purchase{b1} you can withdraw without giving a reason, by writing to kontakt@edimi.ai. We refund the payment {b2}less the value of the tokens you have already spent{b3} - unspent tokens are refunded in full. The right does not lapse because a few days passed; it lapses only for content already delivered, meaning the tokens you used. Details in §11 of the Terms.',
    },
    deleteAccount: {
      q: 'What happens to my files if I delete my account?',
      // The plan's wording (artboard 02): three sentences instead of five, same fact.
      a: 'We delete your files and edits within {b0}30 days{b1} of the account being removed. We keep accounting records longer, as the law requires. Download your edits before that.',
    },
    reportContent: {
      q: 'Someone used my song or my likeness. What can I do?',
      a: 'Report it - {link}. You do not need an EDIMI account. A person reads the report, not an automated filter; we confirm receipt immediately and tell you the decision and the reasons for it separately. We work under article 16 of the Digital Services Act.',
      link: 'the content report form',
    },
    descriptor: {
      q: 'What will I see on my card statement?',
      a: 'The charge appears as {b0}EDIMI{b1}. If you see a payment you do not recognise, email us before disputing it with your bank - we will sort it out faster. Prices on the pricing page include VAT.',
    },
  },
}
